import React, { FunctionComponent, useState } from 'react'
import classNames from 'classnames'
import { ApiRetailerPaymentType } from '@vend/types'
import {
  Button,
  Popover,
  PopoverList,
  PopoverListItem,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { PaymentData } from './PaymentPanel'

export interface PaymentOptionsOverflowProps {
  // Integration-backed payment types (e.g. Cardconnect, Square, Verifone,
  // Worldpay) hidden behind the "More options" trigger.
  paymentTypes: ApiRetailerPaymentType[]
  pay: (payment: ApiRetailerPaymentType, paymentData?: PaymentData) => void
  getPaymentLabel: (paymentType: ApiRetailerPaymentType) => string
  checkIfPaymentDisabled: (paymentType: ApiRetailerPaymentType) => boolean
  arePaymentActionsLocked: boolean
  // Same inputs PaymentOptions uses to style its own payment-type buttons,
  // so the "More options" trigger looks identical to them.
  outstandingBalance: number
  hasLspayPaymentType: boolean
}

/**
 * lspay_growth_experiment_ui: Renders integration-backed payment types (those
 * with a configured processing URL, e.g. Cardconnect, Square, Verifone,
 * Worldpay) behind a "More options…" popover, so the primary payment button
 * grid only shows native/non-integration payment types (Cash, Gift card,
 * Other payment method, etc.) directly.
 */
export const PaymentOptionsOverflow: FunctionComponent<PaymentOptionsOverflowProps> = ({
  paymentTypes,
  pay,
  getPaymentLabel,
  checkIfPaymentDisabled,
  arePaymentActionsLocked,
  outstandingBalance,
  hasLspayPaymentType,
}) => {
  const t = useT()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  if (paymentTypes.length === 0) {
    return null
  }

  const closePopover = () => {
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleSelect = (paymentType: ApiRetailerPaymentType) => {
    pay(paymentType, { selectedFromOverflowMenu: true })
    closePopover()
  }

  // Same variant logic PaymentOptions uses for its own payment-type buttons,
  // so the "More options" trigger looks identical to them.
  const triggerVariant =
    outstandingBalance < 0 ? 'no' : hasLspayPaymentType ? 'supplementary' : 'go'

  // paymentTypes is already alphabetically sorted (inherited from
  // PaymentOptions' sortedPaymentTypes) — filtering preserves that order.
  const searchFilteredPaymentTypes = paymentTypes.filter(paymentType =>
    getPaymentLabel(paymentType)
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase())
  )

  const searchPlaceholder = t('Search payment types', {
    _key: 'Payments.PayScreen.MoreOptions.searchPlaceholder',
    _context:
      'Search input placeholder in the More options payment type dropdown',
  })

  return (
    <div className="payment-options-overflow">
      <WithPopover direction="center">
        <Button
          jumbo
          variant={triggerVariant}
          className={classNames(
            'btn--payment-type payment-options-overflow-trigger',
            {
              'payment-options-overflow-trigger--active': isOpen,
            }
          )}
          onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)}
          disabled={arePaymentActionsLocked}
          aria-expanded={isOpen}
          aria-haspopup="true"
          data-cy="payment-options-more-button"
          data-testid="payment-options-more-button"
          data-track="more-payment-options-button"
        >
          <T
            _str="More options…"
            _key="Payments.PayScreen.MoreOptions.triggerLabel"
            _context="Trigger to reveal additional (integration) payment types on the pay screen"
          />
        </Button>
        {isOpen && (
          <Popover
            onClose={closePopover}
            className="vd-popover--with-list payment-options-overflow-popover"
          >
            <div className="payment-options-overflow-search">
              <input
                type="text"
                className="vd-input"
                autoFocus
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                data-cy="payment-options-search"
                data-testid="payment-options-search"
              />
            </div>
            <div className="payment-options-overflow-list-container">
              {searchFilteredPaymentTypes.length > 0 ? (
                <PopoverList>
                  {searchFilteredPaymentTypes.map((paymentType, index) => (
                    <PopoverListItem
                      key={paymentType.id}
                      index={index}
                      className="payment-options-overflow-item"
                      disabled={
                        arePaymentActionsLocked ||
                        checkIfPaymentDisabled(paymentType)
                      }
                      onItemSelected={() => handleSelect(paymentType)}
                    >
                      {getPaymentLabel(paymentType)}
                    </PopoverListItem>
                  ))}
                </PopoverList>
              ) : (
                <div className="vd-popover-list-empty">
                  <T
                    _str="No matching payment types"
                    _key="Payments.PayScreen.MoreOptions.searchEmptyState"
                    _context="Empty state when the search query in the More options dropdown has no matches"
                  />
                </div>
              )}
            </div>
          </Popover>
        )}
      </WithPopover>
    </div>
  )
}
